import { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/authContext';
import { useEstadoApp } from '../context/EstadoAppContext';
import { useLocalStorage } from '../hooks/useLocalStorage';

const PAGE_SIZE = 50;

const fmtMonto = (val) => {
	if (val === null || val === undefined) return '-';
	return '$' + Number(val).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtFecha = (val) => {
	if (!val) return '-';
	const s = String(val);
	const match = s.match(/^(\d{4}-\d{2}-\d{2})/);
	if (match) return match[1];
	return s;
};

export default function ObrasSinPagosPage() {
	const { role, token } = useContext(AuthContext);
	const navigate = useNavigate();
	const location = useLocation();
	const { cambiarEstadoApp } = useEstadoApp();
	const { setItem } = useLocalStorage();
	const [partidos, setPartidos] = useState([]);
	const [areas, setAreas] = useState([]);
	const [estados, setEstados] = useState([]);
	const [filters, setFilters] = useState({ partido: 'Todos', area: 'Todos', estado: 'Todos', search: '' });
	const [data, setData] = useState({ rows: [], total: 0, page: 1, pageSize: PAGE_SIZE, partidos: [], areas: [], estados: [] });
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [showHabitat, setShowHabitat] = useState(false);
	const [modoRestaurado, setModoRestaurado] = useState(false);
	const [paginaFija, setPaginaFija] = useState(1);
	const fetchSeqRef = useRef(0);

	// Doble click en una fila → abrir la edición de la obra (patrón AnalisisPagos/TablaObras).
	// Al guardar, InicioPage vuelve a /obras-sin-pagos?edicionExitosa=true y se restaura el snapshot.
	const handleRowDoubleClick = (idPartidoObra) => {
		try {
			localStorage.setItem('obrasSinPagosSnapshot', JSON.stringify({
				filters,
				page: data.page,
			}));
		} catch (e) { /* si falla el snapshot, no bloquea la navegación */ }
		setItem('idPartidoObra', idPartidoObra);
		setItem('isEditingPagos', false);
		cambiarEstadoApp('edit_desde_pagos', {
			obra: idPartidoObra,
			origen: 'obras_sin_pagos',
		});
		navigate(`/inicio?id_partido_obra=${encodeURIComponent(idPartidoObra)}`);
	};

	const loadData = useCallback(async (page = 1, opts = {}) => {
		const seq = ++fetchSeqRef.current;
		setLoading(true);
		setError('');
		try {
			const { filtros = filters } = opts;
			const params = new URLSearchParams();
			if (filtros.partido && filtros.partido !== 'Todos') params.append('partido', filtros.partido);
			if (filtros.area && filtros.area !== 'Todos') params.append('area', filtros.area);
			if (filtros.estado && filtros.estado !== 'Todos') params.append('estado', filtros.estado);
			if (filtros.search && filtros.search.trim() !== '') params.append('search', filtros.search.trim());
			params.append('page', page);
			params.append('pageSize', PAGE_SIZE);

			const res = await fetch(`/api/obras-sin-pagos?${params.toString()}`, {
				headers: {
					'Content-Type': 'application/json',
					...(token ? { Authorization: `Bearer ${token}` } : {}),
				},
			});
			if (!res.ok) throw new Error('Error al cargar las obras');
			const result = await res.json();
			if (seq !== fetchSeqRef.current) return; // respuesta vieja, se descarta
			setData(result);
			setPartidos((result.partidos || []).map((p) => ({ value: p, label: p })));
			setAreas((result.areas || []).map((a) => ({ value: a, label: a })));
			setEstados((result.estados || []).map((e) => ({ value: e, label: e })));
		} catch (err) {
			if (seq === fetchSeqRef.current) setError(err.message);
		} finally {
			if (seq === fetchSeqRef.current) setLoading(false);
		}
	}, [filters, token]);

	useEffect(() => {
		if (role !== 'admin') return;
		const base = modoRestaurado ? paginaFija : 1;
		loadData(base);
	}, [role, token, filters, loadData, modoRestaurado, paginaFija]);

	// Al volver de editar (InicioPage navega a /obras-sin-pagos?edicionExitosa=true):
	// restaurar filtros y página del snapshot guardado en el doble clic.
	useEffect(() => {
		const query = new URLSearchParams(location.search);
		if (!query.get('edicionExitosa')) return;

		let snap = null;
		try {
			snap = JSON.parse(localStorage.getItem('obrasSinPagosSnapshot') || 'null');
		} catch (e) { snap = null; }

		if (snap && snap.filters) {
			setPaginaFija(snap.page || 1);
			setModoRestaurado(true);
			setFilters((prev) => ({ ...prev, ...snap.filters }));
		}

		localStorage.removeItem('obrasSinPagosSnapshot');
		const newSearch = new URLSearchParams(location.search);
		newSearch.delete('edicionExitosa');
		window.history.replaceState({}, '', [location.pathname, newSearch.toString()].filter(Boolean).join('?'));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleFilterChange = (name, value) => {
		setModoRestaurado(false);
		setFilters((prev) => ({ ...prev, [name]: value }));
	};

	const resetFilters = () => {
		setModoRestaurado(false);
		setFilters({ partido: 'Todos', area: 'Todos', estado: 'Todos', search: '' });
	};

	const navegarPagina = (n) => {
		setModoRestaurado(false);
		loadData(n);
	};

	const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
	const colCount = showHabitat ? 9 : 6;

	if (role !== 'admin') {
		return (
			<div className="p-8 text-center text-gray-500">
				Acceso denegado. Solo administradores.
			</div>
		);
	}

	return (
		<div className="p-6 w-full max-w-full">
			<h1 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-1">
				Obras sin pagos
			</h1>
			<p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
				Obras con expediente de Proyecto y de Rendición cargados pero sin ningún pago registrado.
			</p>

			{/* Filtros */}
			<div className="mb-4 rounded-xl border border-gray-200 bg-white dark:bg-gray-800 shadow-sm p-4 border-b border-gray-100 dark:border-gray-700">
				<div className="flex flex-wrap gap-4">
					<div className="flex-1 min-w-[160px]">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Partido</label>
						<select
							value={filters.partido}
							onChange={(e) => handleFilterChange('partido', e.target.value)}
							className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-100"
						>
							<option value="Todos">Todos los partidos</option>
							{partidos.map((p) => (
								<option key={p.value} value={p.value}>{p.label}</option>
							))}
						</select>
					</div>

					<div className="flex-1 min-w-[140px]">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Área</label>
						<select
							value={filters.area}
							onChange={(e) => handleFilterChange('area', e.target.value)}
							className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-100"
						>
							<option value="Todos">Todas las áreas</option>
							{areas.map((a) => (
								<option key={a.value} value={a.value}>{a.label}</option>
							))}
						</select>
					</div>

					<div className="flex-1 min-w-[150px]">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Estado de Obra</label>
						<select
							value={filters.estado}
							onChange={(e) => handleFilterChange('estado', e.target.value)}
							className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-100"
						>
							<option value="Todos">Todos los estados</option>
							{estados.map((e) => (
								<option key={e.value} value={e.value}>{e.label}</option>
							))}
						</select>
					</div>
				</div>

				<div className="mt-3 flex flex-wrap items-center gap-4">
					<div className="min-w-[260px] flex-1">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Buscar (obra / expediente)</label>
						<input
							type="text"
							value={filters.search}
							onChange={(e) => handleFilterChange('search', e.target.value)}
							placeholder="Ej: 055-062 o EX-2020-..."
							className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-100"
						/>
					</div>
					<div className="flex items-center gap-6">
						<label className="flex items-center gap-2 cursor-pointer select-none">
							<input
								type="checkbox"
								checked={showHabitat}
								onChange={(e) => setShowHabitat(e.target.checked)}
								className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
							/>
							<span className="text-sm text-gray-700 dark:text-gray-300">
								Mostrar Habitat (rojas)
							</span>
						</label>
					</div>
					<div className="flex items-center gap-4">
						<button
							onClick={resetFilters}
							className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
						>
							Resetear filtros
						</button>
						<span className="text-xs text-gray-400">
							{data.total.toLocaleString('es-AR')} obras
						</span>
					</div>
				</div>
			</div>

			{loading && <p className="text-gray-500 mb-4">Cargando datos...</p>}
			{error && <p className="text-red-600 mb-4">{error}</p>}

			{/* Tabla */}
			<div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-x-auto">
				<table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
					<thead className="bg-gray-50 dark:bg-gray-700">
						<tr>
							<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">ID Partido Obra</th>
							<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Partido</th>
							<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Área</th>
							<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Exp. Proyecto</th>
							<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Exp. Rendición</th>
							<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Estado</th>
							{showHabitat && (
								<>
									<th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20">Monto Habitat</th>
									<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20">Fecha Habitat</th>
									<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20">Instr Habitat</th>
								</>
							)}
						</tr>
					</thead>
					<tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
						{data.rows.map((r) => (
							<tr
								key={r.id_tabla_obras}
								className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-150"
								title="Doble clic para editar la obra"
								onDoubleClick={() => handleRowDoubleClick(r.id_partido_obra)}
							>
								<td className="px-4 py-2 text-gray-700 dark:text-gray-300">{r.id_partido_obra}</td>
								<td className="px-4 py-2 text-gray-700 dark:text-gray-300">{r.partido || '-'}</td>
								<td className="px-4 py-2 text-gray-700 dark:text-gray-300">{r.area || '-'}</td>
								<td className="px-4 py-2 text-gray-700 dark:text-gray-300">{r.exp_proyecto || '-'}</td>
								<td className="px-4 py-2 text-gray-700 dark:text-gray-300">{r.exp_rendicion || '-'}</td>
								<td className="px-4 py-2 text-gray-700 dark:text-gray-300">{r.estado || '-'}</td>
								{showHabitat && (
									<>
										<td className="px-4 py-2 text-right text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20">{fmtMonto(r.monto_transferido)}</td>
										<td className="px-4 py-2 text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20">{fmtFecha(r.fecha_habitat)}</td>
										<td className="px-4 py-2 text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20">{r.instr_habitat || '-'}</td>
									</>
								)}
							</tr>
						))}
						{!loading && data.rows.length === 0 && (
							<tr>
								<td colSpan={colCount} className="px-4 py-8 text-center text-gray-400">
									No hay obras que coincidan con los filtros.
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			{/* Paginación */}
			{totalPages > 1 && (
				<div className="mt-4 flex items-center justify-center gap-2">
					<button
						onClick={() => navegarPagina(data.page - 1)}
						disabled={data.page <= 1 || loading}
						className="px-3 py-1 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
					>
						Anterior
					</button>
					<span className="text-sm text-gray-500">
						Página {data.page} de {totalPages}
					</span>
					<button
						onClick={() => navegarPagina(data.page + 1)}
						disabled={data.page >= totalPages || loading}
						className="px-3 py-1 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
					>
						Siguiente
					</button>
				</div>
			)}
		</div>
	);
}